import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { getPreferredOrigin } from '../../utils/metaTagsUtils';

interface MetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
}

/**
 * Comprehensive MetaTags component that injects all necessary meta tags
 * This replaces the need to have meta tags in the HTML file
 */
const MetaTags: React.FC<MetaTagsProps> = ({
  title = 'TapDonkey',
  description = 'TapDonkey - Create custom interactive counters for events, goals, and statistics. Share your counters with anyone for free.',
  image = '/images/favicon/web-app-manifest-512x512.png',
  url = '/',
  type = 'website'
}) => {
  const baseUrl = getPreferredOrigin();
  const fullImageUrl = `${baseUrl}${image}`;
  const fullUrl = `${baseUrl}${url}`;

  return (
    <Helmet>
      {/* Primary favicon references */}
      <link rel="icon" href="/images/favicon/favicon.svg" type="image/svg+xml" />
      <link rel="icon" href="/images/favicon/favicon-96x96.png" type="image/png" />
      <link rel="shortcut icon" href="/images/favicon/favicon.ico" />
      
      {/* Apple Touch Icons */}
      <link rel="apple-touch-icon" sizes="180x180" href="/images/favicon/apple-touch-icon.png" />
      
      {/* Web App Manifest */}
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Basic Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#2C2E43" />
      <meta name="description" content={description} />
      <meta name="author" content="Formilis Studio" />
      <meta name="creator" content="Formilis Studio" />
      <meta name="publisher" content="Formilis Studio" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content="TapDonkey Logo" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content="TapDonkey Logo" />
      
      {/* Add Inter font from Google Fonts for modern typography */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      
      <title>{title}</title>
    </Helmet>
  );
};

export default MetaTags; 